import {
  FilterXIcon,
  MailWarningIcon,
  RefreshCwIcon,
  SearchIcon,
  UserPlusIcon,
  UsersIcon,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserStatus, type ManagedUser } from '@/api/users'
import { useApiErrorMessage } from '@/api/errors'
import { useAuth } from '@/auth/useAuth'
import { ClearFiltersButton } from '@/components/ClearFiltersButton'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardFooter } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ActivatableRow } from '@/components/layout/ActivatableRow'
import { PageHeader } from '@/components/layout/PageHeader'
import { Pagination } from '@/components/layout/Pagination'
import {
  EDGE_PADDED_TABLE,
  FULL_HEIGHT_CARD,
  PHONE_SUMMARY,
  PHONE_WRAPPING_CELL,
  SCROLLING_TABLE,
  STICKY_TABLE_HEAD,
  WIDE_ONLY,
} from '@/components/layout/tableStyles'
import { EditUserDialog } from '@/components/users/EditUserDialog'
import { InviteUserDialog } from '@/components/users/InviteUserDialog'
import { SetPasswordDialog } from '@/components/users/SetPasswordDialog'
import { UserRowActions } from '@/components/users/UserRowActions'
import { UserStatusBadge } from '@/components/users/UserStatusBadge'
import { ROLE_LABEL_KEYS } from '@/components/users/roleLabels'
import { bareRole, fullName } from '@/lib/userDisplay'
import { cn } from '@/lib/utils'
import { useUserList } from '@/hooks/useUserList'

const COLUMN_COUNT = 4

export function UsersPage() {
  const { t } = useTranslation()
  const { user: me } = useAuth()
  const errorMessage = useApiErrorMessage()
  const list = useUserList()

  const [inviting, setInviting] = useState(false)
  // One dialog each, driven by which user they point at — not one dialog per row.
  const [editing, setEditing] = useState<ManagedUser | null>(null)
  const [settingPasswordFor, setSettingPasswordFor] = useState<ManagedUser | null>(null)
  const [activating, setActivating] = useState<ManagedUser | null>(null)

  const users = list.page?.users ?? []
  const total = list.page?.total ?? 0

  return (
    <TooltipProvider>
      <PageHeader title={t('users.title')}>
        <Button onClick={() => setInviting(true)}>
          <UserPlusIcon />
          {t('users.actions.invite')}
        </Button>
      </PageHeader>

      <Card className={FULL_HEIGHT_CARD}>
        <div className="flex flex-wrap items-center gap-3 border-b p-4 shrink-0">
          <div className="relative min-w-56 flex-1">
            <SearchIcon
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
              aria-hidden
            />
            <Input
              type="search"
              className="pl-8"
              aria-label={t('users.searchLabel')}
              placeholder={t('users.searchPlaceholder')}
              value={list.search}
              onChange={(event) => list.setSearch(event.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="include-deleted"
              checked={list.includeDeleted}
              onCheckedChange={(checked) => list.setIncludeDeleted(checked === true)}
            />
            <Label htmlFor="include-deleted" className="text-sm font-normal">
              {t('users.includeDeleted')}
            </Label>
          </div>

          <ClearFiltersButton
            active={list.filtered}
            label={t('common.clearFilters')}
            onClick={list.clearFilters}
          />

          <Button
            variant="outline"
            size="icon"
            aria-label={t('users.reload')}
            onClick={list.reload}
          >
            <RefreshCwIcon />
          </Button>
        </div>

        {list.error ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <Alert variant="destructive">
              <AlertTitle>{t('users.loadFailed')}</AlertTitle>
              <AlertDescription>
                {errorMessage(list.error, t('common.unexpectedError'))}
              </AlertDescription>
              <AlertAction>
                <Button variant="outline" size="sm" onClick={list.reload}>
                  {t('users.retry')}
                </Button>
              </AlertAction>
            </Alert>
          </div>
        ) : (
          <Table
            className={EDGE_PADDED_TABLE}
            containerClassName={SCROLLING_TABLE}
            // No sortable headings here: accounts live in Keycloak, which answers in the order it
            // keeps them and takes no column to order by.
            //
            // The rows on screen are the previous page while the next one loads. `aria-busy` is
            // what says so to a screen reader; the dimming below is what says it to everybody else.
            aria-busy={list.loading}
          >
            <TableHeader className={STICKY_TABLE_HEAD}>
              <TableRow>
                <TableHead>{t('users.columns.name')}</TableHead>
                <TableHead className={WIDE_ONLY}>{t('users.columns.role')}</TableHead>
                <TableHead className={WIDE_ONLY}>{t('users.columns.status')}</TableHead>
                <TableHead className="w-0 text-right">
                  <span className="sr-only">{t('users.columns.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody
              // Held back while the next page is on its way, rather than blanked: the rows are
              // still true, they are just no longer the answer to what was asked.
              className={list.loading && users.length > 0 ? 'opacity-60 transition-opacity' : undefined}
            >
              {/* Only while there is nothing to show. Repainting a populated table as skeletons
                  on every keystroke is worse than leaving the old rows up. */}
              {list.loading && users.length === 0 && <LoadingRows />}

              {!list.loading && users.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={COLUMN_COUNT} className="h-40">
                    <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 text-center">
                      <UsersIcon className="size-6" aria-hidden />
                      <p className="text-sm">
                        {list.search
                          ? t('users.emptySearch', { search: list.search })
                          : t('users.empty')}
                      </p>
                      {/* The way out of the state somebody is looking at: back to the whole list
                          when a filter emptied it, and the first invitation when there is none. */}
                      {list.filtered ? (
                        <Button variant="outline" size="sm" onClick={list.clearFilters}>
                          <FilterXIcon />
                          {t('common.clearFilters')}
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setInviting(true)}>
                          <UserPlusIcon />
                          {t('users.actions.invite')}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {users.map((user) => {
                const role = bareRole(user.role)
                const isSelf = user.id === me?.id
                const roleBadge = role ? (
                  <Badge variant={role === 'ADMIN' ? 'default' : 'secondary'}>
                    {t(ROLE_LABEL_KEYS[role])}
                  </Badge>
                ) : (
                  // Possible after the MANAGER/USER roles were dropped: the account kept an
                  // assignment the realm no longer has, and now has none.
                  <Badge variant="destructive">{t('users.noRole')}</Badge>
                )
                const cells = (
                  <>
                    <TableCell className={PHONE_WRAPPING_CELL}>
                      <div className="flex flex-col">
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium">
                          {fullName(user) || (
                            <span className="text-muted-foreground italic">
                              {t('users.noName')}
                            </span>
                          )}
                          {isSelf && (
                            <Badge variant="outline" className="font-normal">
                              {t('users.you')}
                            </Badge>
                          )}
                        </span>
                        <span className="text-muted-foreground flex flex-wrap items-center gap-x-1.5 text-xs break-all">
                          {user.email ?? t('common.none')}
                          {/* Not on an invited account: its status badge already says the address
                              is unproved, and saying it twice makes the one row that is unusual
                              harder to spot, not easier. */}
                          {user.email && !user.emailVerified && user.status !== UserStatus.INVITED && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-500">
                                  <MailWarningIcon className="size-3.5" aria-hidden />
                                  {t('users.emailUnverified')}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>{t('users.emailUnverifiedHint')}</TooltipContent>
                            </Tooltip>
                          )}
                        </span>
                        <span className={cn(PHONE_SUMMARY, 'mt-1.5 gap-y-1')}>
                          {roleBadge}
                          <UserStatusBadge status={user.status} />
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className={WIDE_ONLY}>{roleBadge}</TableCell>
                    <TableCell className={WIDE_ONLY}>
                      <UserStatusBadge status={user.status} />
                    </TableCell>
                    <TableCell
                      className="text-right"
                      // The row opens the account; the menu inside it — and the confirmations it
                      // portals out, whose clicks React still bubbles through here — must not.
                      onClick={(event) => event.stopPropagation()}
                    >
                      <UserRowActions
                        user={user}
                        isSelf={isSelf}
                        onEdit={() => setEditing(user)}
                        onSetPassword={() => setSettingPasswordFor(user)}
                        onActivate={() => setActivating(user)}
                        onChanged={list.reload}
                      />
                    </TableCell>
                  </>
                )
                // A deleted account has nothing to edit until it is restored — its menu offers
                // only that — so its row opens nothing either.
                return user.status === UserStatus.DELETED ? (
                  <TableRow key={user.id} data-testid={`user-${user.id}`}>
                    {cells}
                  </TableRow>
                ) : (
                  <ActivatableRow
                    key={user.id}
                    data-testid={`user-${user.id}`}
                    onActivate={() => setEditing(user)}
                  >
                    {cells}
                  </ActivatableRow>
                )
              })}
            </TableBody>
          </Table>
        )}

        {!list.error && total > 0 && (
          <CardFooter className="block shrink-0">
            <Pagination
              pageIndex={list.pageIndex}
              pageCount={list.pageCount}
              pageSize={list.pageSize}
              range={list.range}
              onPageChange={list.setPageIndex}
              onPageSizeChange={list.setPageSize}
            />
          </CardFooter>
        )}
      </Card>

      <InviteUserDialog open={inviting} onOpenChange={setInviting} onInvited={list.reload} />
      <EditUserDialog
        user={editing}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={list.reload}
      />
      <SetPasswordDialog
        user={settingPasswordFor}
        onOpenChange={(open) => !open && setSettingPasswordFor(null)}
        onDone={list.reload}
      />
      <SetPasswordDialog
        user={activating}
        activating
        onOpenChange={(open) => !open && setActivating(null)}
        onDone={list.reload}
      />
    </TooltipProvider>
  )
}

function LoadingRows() {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <TableRow key={index} className="hover:bg-transparent">
          <TableCell colSpan={COLUMN_COUNT} className="py-3">
            <Skeleton className="h-8 w-full" />
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}
